/**
 * Invitation Store
 * 
 * Pinia store for managing invitation state and operations.
 * Encapsulates state management logic for invitations.
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import invitationApi from '../services/invitationApi';

export const useInvitationStore = defineStore('invitation', () => {
  // State - Private reactive state
  const invitations = ref([]);
  const currentInvitation = ref(null);
  const stats = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // Getters - Computed properties, read-only access
  const pendingInvitations = computed(() => {
    return invitations.value.filter(inv => inv.status === 'pending');
  });

  const acceptedInvitations = computed(() => {
    return invitations.value.filter(inv => inv.status === 'accepted');
  });

  const expiredInvitations = computed(() => {
    return invitations.value.filter(inv => inv.status === 'expired');
  });

  // Actions - Public methods
  /**
   * Send a single invitation
   * @param {Object} data - Invitation data
   * @returns {Promise<Object>} Result
   */
  const sendInvitation = async (data) => {
    console.log('[DEBUG] InvitationStore.sendInvitation: Starting');
    console.log('[DEBUG] InvitationStore.sendInvitation: data:', data);
    console.log('[DEBUG] InvitationStore.sendInvitation: invitationApi type:', typeof invitationApi);
    console.log('[DEBUG] InvitationStore.sendInvitation: invitationApi.sendInvitation type:', typeof invitationApi?.sendInvitation);
    
    // Check token availability before proceeding
    const tokenCheck = {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
      user: localStorage.getItem('user')
    };
    console.log('[DEBUG] InvitationStore.sendInvitation: Authentication state before request:', {
      accessTokenPresent: !!tokenCheck.accessToken,
      refreshTokenPresent: !!tokenCheck.refreshToken,
      userPresent: !!tokenCheck.user,
      accessTokenPreview: tokenCheck.accessToken ? tokenCheck.accessToken.substring(0, 20) + '...' : null,
      userData: tokenCheck.user ? JSON.parse(tokenCheck.user) : null
    });
    
    loading.value = true;
    error.value = null;

    try {
      if (!invitationApi || typeof invitationApi.sendInvitation !== 'function') {
        console.error('[DEBUG] InvitationStore.sendInvitation: invitationApi.sendInvitation is not a function!');
        throw new Error('Invitation API service is not properly initialized');
      }
      
      console.log('[DEBUG] InvitationStore.sendInvitation: Calling invitationApi.sendInvitation');
      const result = await invitationApi.sendInvitation(data);
      console.log('[DEBUG] InvitationStore.sendInvitation: Result received:', result);
      
      if (result.success) {
        // Add to invitations list
        invitations.value.unshift(result.invitation);
      }
      
      return {
        success: true,
        invitation: result.invitation,
        message: result.message,
        emailQueued: result.emailQueued || false,
        queuePosition: result.queuePosition || null,
        estimatedWaitTime: result.estimatedWaitTime || null,
        invitationCode: result.invitationCode || result.invitation?.code || null,
        quotaExhausted: result.quotaExhausted || false,
        queued: result.queued || false
      };
    } catch (err) {
      console.error('[DEBUG] InvitationStore.sendInvitation: Error caught:', err);
      console.error('[DEBUG] InvitationStore.sendInvitation: Error message:', err?.message);
      console.error('[DEBUG] InvitationStore.sendInvitation: Error stack:', err?.stack);
      error.value = err.message || 'Failed to send invitation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Send bulk invitations
   * @param {Array} invitationsList - Array of invitation data
   * @param {Object} options - Bulk options
   * @returns {Promise<Object>} Result
   */
  const sendBulkInvitations = async (invitationsList, options = {}) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.sendBulkInvitations(invitationsList, options);
      
      // Refresh invitations list
      await fetchInvitations();
      
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to send bulk invitations';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch user's invitations with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Invitations with pagination
   */
  const fetchInvitations = async (filters = {}) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.getInvitations(filters);
      
      if (result.success) {
        // If page 1, replace list; otherwise append (for infinite scroll if needed)
        if (filters.page === 1 || !filters.page) {
          invitations.value = result.invitations;
        } else {
          invitations.value.push(...result.invitations);
        }
        
        return {
          invitations: result.invitations,
          pagination: result.pagination
        };
      }
      
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to fetch invitations';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch single invitation by ID
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} Invitation
   */
  const fetchInvitation = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.getInvitation(id);
      
      if (result.success) {
        currentInvitation.value = result.invitation;
        return result.invitation;
      }
      
      return null;
    } catch (err) {
      error.value = err.message || 'Failed to fetch invitation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Resend invitation email
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} Result
   */
  const resendInvitation = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.resendInvitation(id);
      
      if (result.success) {
        // Update invitation in list
        const index = invitations.value.findIndex(inv => inv._id === id || inv.id === id);
        if (index !== -1) {
          invitations.value[index] = result.invitation;
        }
      }
      
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to resend invitation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Cancel invitation
   * @param {string} id - Invitation ID
   * @returns {Promise<Object>} Result
   */
  const cancelInvitation = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.cancelInvitation(id);
      
      if (result.success) {
        // Remove from list or update status
        const index = invitations.value.findIndex(inv => inv._id === id || inv.id === id);
        if (index !== -1) {
          invitations.value[index].status = 'cancelled';
        }
      }
      
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to cancel invitation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Validate invitation code (public)
   * @param {string} code - Invitation code
   * @returns {Promise<Object>} Validation result
   */
  const validateInvitationCode = async (code) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.validateInvitationCode(code);
      
      if (result.valid) {
        currentInvitation.value = result.invitation;
      }
      
      return result;
    } catch (err) {
      error.value = err.message || 'Failed to validate invitation';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Accept invitation and create account
   * @param {string} code - Invitation code
   * @param {Object} data - Registration data (password, username, firstName, lastName)
   * @returns {Promise<Object>} Result with user and tokens
   */
  const acceptInvitation = async (code, data) => {
    console.log('[DEBUG] InvitationStore.acceptInvitation: Starting');
    console.log('[DEBUG] InvitationStore.acceptInvitation: Code:', code);
    console.log('[DEBUG] InvitationStore.acceptInvitation: Data:', {
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      passwordLength: data.password?.length || 0
    });

    loading.value = true;
    error.value = null;

    try {
      console.log('[DEBUG] InvitationStore.acceptInvitation: Calling invitationApi.acceptInvitation...');
      const startTime = Date.now();
      const result = await invitationApi.acceptInvitation(code, data);
      const elapsedTime = Date.now() - startTime;
      
      console.log('[DEBUG] InvitationStore.acceptInvitation: API call completed after', elapsedTime, 'ms');
      console.log('[DEBUG] InvitationStore.acceptInvitation: Result:', {
        success: result.success,
        hasUser: !!result.user,
        hasTokens: !!result.tokens,
        message: result.message
      });
      
      if (result.success) {
        console.log('[DEBUG] InvitationStore.acceptInvitation: Success - clearing current invitation');
        // Clear current invitation
        currentInvitation.value = null;
      } else {
        console.warn('[DEBUG] InvitationStore.acceptInvitation: Result success is false');
      }
      
      console.log('[DEBUG] InvitationStore.acceptInvitation: Returning result');
      return result;
    } catch (err) {
      console.error('[DEBUG] InvitationStore.acceptInvitation: ERROR caught:', err);
      console.error('[DEBUG] InvitationStore.acceptInvitation: Error details:', {
        message: err?.message,
        stack: err?.stack,
        status: err?.status,
        code: err?.code
      });
      error.value = err.message || 'Failed to accept invitation';
      throw err;
    } finally {
      console.log('[DEBUG] InvitationStore.acceptInvitation: Entering finally block, resetting loading');
      loading.value = false;
      console.log('[DEBUG] InvitationStore.acceptInvitation: Completed');
    }
  };

  /**
   * Fetch invitation statistics
   * @returns {Promise<Object>} Statistics
   */
  const fetchStats = async () => {
    loading.value = true;
    error.value = null;

    try {
      const result = await invitationApi.getInvitationStats();
      
      if (result.success) {
        stats.value = result.stats;
      }
      
      return result.stats;
    } catch (err) {
      error.value = err.message || 'Failed to fetch statistics';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Clear error
   */
  const clearError = () => {
    error.value = null;
  };

  /**
   * Reset store state
   */
  const reset = () => {
    invitations.value = [];
    currentInvitation.value = null;
    stats.value = null;
    loading.value = false;
    error.value = null;
  };

  // Expose public interface
  return {
    // State (read-only reactive refs)
    invitations: computed(() => invitations.value),
    currentInvitation: computed(() => currentInvitation.value),
    stats: computed(() => stats.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    
    // Actions
    sendInvitation,
    sendBulkInvitations,
    fetchInvitations,
    fetchInvitation,
    resendInvitation,
    cancelInvitation,
    validateInvitationCode,
    acceptInvitation,
    fetchStats,
    clearError,
    reset,
    
    // Getters
    pendingInvitations,
    acceptedInvitations,
    expiredInvitations
  };
});
